/** Целочисленное распределение total по частям пропорционально weights; последняя часть — остаток. */
import moment from "moment";

export function splitProportionalInt(total: number, weights: number[]): number[] {
  const s = weights.reduce((a, b) => a + b, 0);
  if (s <= 0 || weights.length === 0) {
    return weights.map(() => 0);
  }
  const out: number[] = [];
  let used = 0;
  for (let i = 0; i < weights.length - 1; i++) {
    const v = Math.floor((total * weights[i]) / s);
    out.push(v);
    used += v;
  }
  out.push(total - used);
  return out;
}

export function ceilContainersFromMonthPlanKg(
  monthPlanKg: number,
  containerWeightKg: number
): number {
  const value = Number(monthPlanKg);
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  const weight = Number(containerWeightKg);
  if (!Number.isFinite(weight) || weight <= 0) {
    return Math.ceil(value);
  }
  return Math.ceil(value / weight);
}

export function monthPlanKgFromContainers(
  containers: number,
  containerWeightKg: number
): number {
  const count = Number(containers);
  if (!Number.isFinite(count) || count <= 0) {
    return 0;
  }
  const weight = Number(containerWeightKg);
  if (!Number.isFinite(weight) || weight <= 0) {
    return count;
  }
  return Math.round(count * weight * 100) / 100;
}

export interface WeekInMonth {
  weekKey: string;
  daysInMonth: number;
}

/** Пересечение ISO-недели с календарным месяцем отчёта (для сохранения week_plan). */
export function weekBoundsInReportMonth(
  weekStart: string,
  weekEnd: string,
  targetDates: any
): { start: string; end: string } {
  if (
    !weekStart ||
    !weekEnd ||
    !targetDates
  ) {
    return { start: weekStart, end: weekEnd };
  }
  const ms = moment(targetDates).startOf("month");
  const me = moment(targetDates).endOf("month");
  const ws = moment(weekStart).startOf("day");
  const we = moment(weekEnd).startOf("day");
  if (!ws.isValid() || !we.isValid()) {
    return { start: weekStart, end: weekEnd };
  }
  const weekLo = ws.isAfter(we) ? we : ws;
  const weekHi = ws.isAfter(we) ? ws : we;
  const s = moment.max(weekLo, ms);
  const e = moment.min(weekHi, me);
  if (e.isBefore(s)) {
    return {
      start: weekLo.format("YYYY-MM-DD"),
      end: weekHi.format("YYYY-MM-DD")
    };
  }
  return {
    start: s.format("YYYY-MM-DD"),
    end: e.format("YYYY-MM-DD")
  };
}

/** Равномерно распределяет целое число тар по неделям, попадающим в отчётный месяц. */
export function distributeContainersEvenlyAcrossWeeks(
  totalContainers: number,
  weeks: WeekInMonth[]
): Record<string, number> {
  const result: Record<string, number> = {};
  weeks.forEach(w => {
    result[w.weekKey] = 0;
  });
  if (totalContainers <= 0 || weeks.length === 0) {
    return result;
  }

  const active = weeks.filter(w => w.daysInMonth > 0);
  const targets = active.length > 0 ? active : weeks;
  const weekCount = targets.length;
  const perWeek = Math.ceil(totalContainers / weekCount);
  let remaining = totalContainers;

  for (let i = 0; i < weekCount - 1; i++) {
    result[targets[i].weekKey] = perWeek;
    remaining -= perWeek;
  }
  result[targets[weekCount - 1].weekKey] = remaining;

  return result;
}

export function buildMonthPlanDistribution(
  monthPlanKg: number,
  containerWeightKg: number,
  weeks: WeekInMonth[]
): {
  totalContainers: number;
  monthPlanKg: number;
  weekContainers: Record<string, number>;
} {
  const totalContainers = ceilContainersFromMonthPlanKg(
    monthPlanKg,
    containerWeightKg
  );
  const normalizedMonthKg = monthPlanKgFromContainers(
    totalContainers,
    containerWeightKg
  );
  const weekContainers = distributeContainersEvenlyAcrossWeeks(
    totalContainers,
    weeks
  );
  return {
    totalContainers,
    monthPlanKg: normalizedMonthKg,
    weekContainers
  };
}
